import { singularityStore } from "@/store/store";
import { TreeNode } from "../tree";

type AIJobStatus = "pending" | "running" | "completed" | "failed";

export type AIJobStateForUI = {
  affectedElement: TreeNode["id"] | false;
  status: AIJobStatus;
  id: number;
  jobName: string;
};

export type AIJobCallback = (
  abortController: AbortController
) => Promise<unknown>;

type AIJob = {
  id: number;
  status: AIJobStatus;
  callback: AIJobCallback;
  abortController: AbortController;
  jobName: string;
  affectedElement: TreeNode["id"] | false;
};

class AIJobController {
  private jobs: AIJob[] = [];

  MAX_JOBS = 3;


  async runPendingJobs() {
    if (
      !this.jobs.some(job => job.status === "pending") ||
      this.jobs.length === 0
    ) {
      return;
    }

    const runningJobs = this.jobs.filter(
      job => job.status === "running"
    ).length;
    if (runningJobs >= this.MAX_JOBS) {
      return;
    }

    const pendingJobs = this.jobs
      .filter(job => job.status === "pending")
      .slice(0, this.MAX_JOBS - runningJobs);

    pendingJobs.forEach(job => {
      this.runJob(job.id);
    });
  }

  addJob(
    callback: AIJobCallback,
    jobName: string,
    affectedElement: TreeNode["id"] | false
  ) {
    const abortController = new AbortController();
    const jobId = this.jobs.length + 1;

    const newJob: AIJob = {
      jobName: jobName,
      id: jobId,
      status: "pending",
      callback,
      abortController,
      affectedElement
    };

    this.jobs.push(newJob);
    this.runPendingJobs();
    this.updateStore();
  }

  async runJob(jobId: number) {
    const jobIndex = this.jobs.findIndex(j => j.id === jobId);
    if (jobIndex === -1) return;

    this.jobs[jobIndex].status = "running";
    this.updateStore();

    await this.jobs[jobIndex].callback(this.jobs[jobIndex].abortController);

    this.jobs[jobIndex].status = "completed";

    this.runPendingJobs();
    this.updateStore();
  }

  async abortJob(jobId: number) {
    const jobIndex = this.jobs.findIndex(j => j.id === jobId);
    if (jobIndex === -1) return;
    this.jobs[jobIndex].abortController.abort();
    this.jobs[jobIndex].status = "failed";

    this.runPendingJobs();
    this.updateStore();
  }

  clearAllJobs() {
    this.jobs = [];
    this.updateStore();
  }

  updateStore() {
    const jobsForUI: AIJobStateForUI[] = this.jobs.map(job => ({
      status: job.status,
      id: job.id,
      jobName: job.jobName,
      affectedElement: job.affectedElement
    }));

    singularityStore.setAIJobs(jobsForUI);
  }
}

export const aiJobController = new AIJobController();
